import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Header from '@/components/Header';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportData = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('members');
  const [importResults, setImportResults] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel' || 
                 file.name.endsWith('.xlsx'))) {
      setSelectedFile(file);
      setImportResults(null);
    } else {
      toast.error('Please select an Excel (.xlsx) file');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/import/${importType}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      toast.success(`Import completed! ${response.data.imported} records imported.`);
      setSelectedFile(null);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type) => {
    let headers = [];
    let filename = '';

    switch(type) {
      case 'members':
        headers = ['member_id', 'name', 'initials', 'designation', 'company_name', 'contact_no', 'email', 'address', 'pincode', 'state', 'district', 'expiry_date', 'member_type', 'customer_type', 'plan_type', 'database_type', 'subscription_duration', 'amount_paid', 'status', 'notes', 'category', 'delivery'];
        filename = 'member_import_template.xlsx';
        break;
      case 'advertisers':
        headers = ['advertiser_id', 'name', 'initials', 'designation', 'company_name', 'contact_no', 'email', 'address', 'pincode', 'state', 'district', 'size', 'duration', 'amount', 'payment_status', 'status', 'start_date', 'end_date', 'delivery_mode', 'notes', 'print_schedule_type', 'schedule_type', 'magazine'];
        filename = 'advertiser_import_template.xlsx';
        break;
      case 'bills':
        headers = ['bill_no', 'fy', 'document_type', 'magazine', 'type', 'product', 'po_wo_no', 'proforma_invoice_no', 'entity_id', 'initial', 'billing_name', 'bill_receipt_name', 'billing_state', 'district', 'pincode', 'gst_no', 'occasion', 'gross_amount', 'sgst_amount', 'cgst_amount', 'igst_amount', 'total_amount', 'amount_in_words', 'terms_conditions'];
        filename = 'bill_import_template.xlsx';
        break;
      case 'dispatches':
        headers = ['id', 'member_id', 'name', 'docket_no', 'magazine', 'month', 'year', 'dispatch_date', 'courier_type'];
        filename = 'dispatch_import_template.xlsx';
        break;
    }

    // Create Excel workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    
    // Generate Excel file
    XLSX.writeFile(wb, filename);
    toast.success('Excel template downloaded!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }} data-testid="import-title">
            Import Data
          </h1>
        </div>

        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#7c3aed' }}>
            Select Import Type
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setImportType('members')}
              className={`p-6 rounded-lg border-2 transition-all ${
                importType === 'members' 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <FileSpreadsheet className="mx-auto mb-2" size={32} style={{ color: '#7c3aed' }} />
              <p className="font-semibold">Members</p>
            </button>

            <button
              onClick={() => setImportType('advertisers')}
              className={`p-6 rounded-lg border-2 transition-all ${
                importType === 'advertisers' 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <FileSpreadsheet className="mx-auto mb-2" size={32} style={{ color: '#7c3aed' }} />
              <p className="font-semibold">Advertisers</p>
            </button>

            <button
              onClick={() => setImportType('bills')}
              className={`p-6 rounded-lg border-2 transition-all ${
                importType === 'bills' 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <FileSpreadsheet className="mx-auto mb-2" size={32} style={{ color: '#7c3aed' }} />
              <p className="font-semibold">Bills</p>
            </button>

            <button
              onClick={() => setImportType('dispatches')}
              className={`p-6 rounded-lg border-2 transition-all ${
                importType === 'dispatches' 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <FileSpreadsheet className="mx-auto mb-2" size={32} style={{ color: '#7c3aed' }} />
              <p className="font-semibold">Dispatches</p>
            </button>
          </div>

          <div className="mb-6">
            <Button
              onClick={() => downloadTemplate(importType)}
              variant="outline"
              className="w-full"
              data-testid="download-template"
            >
              <Download className="mr-2" size={18} />
              Download Excel Template for {importType}
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Select Excel File</Label>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="mt-2 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-purple-50 file:text-purple-700
                  hover:file:bg-purple-100
                  cursor-pointer"
                data-testid="file-input"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-green-600">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>

            <Button
              onClick={handleImport}
              disabled={loading || !selectedFile}
              className="w-full btn-primary"
              style={{ backgroundColor: '#7c3aed' }}
              data-testid="import-button"
            >
              <Upload className="mr-2" size={18} />
              {loading ? 'Importing...' : 'Import Data'}
            </Button>
          </div>
        </div>

        {importResults && (
          <div className="form-section">
            <h2 className="text-xl font-semibold mb-4" style={{ color: importResults.errors.length > 0 ? '#dc2626' : '#059669' }}>
              Import Results
            </h2>
            
            <div className="space-y-3">
              <p className="text-sm">
                <strong>Total Rows:</strong> {importResults.total_rows}
              </p>
              <p className="text-sm text-green-600">
                <strong>Successfully Imported:</strong> {importResults.imported}
              </p>
              
              {importResults.errors.length > 0 && (
                <div>
                  <p className="text-sm text-red-600 font-semibold mb-2">
                    Errors ({importResults.errors.length}):
                  </p>
                  <div className="bg-red-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <p key={index} className="text-xs text-red-700 mb-1">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 p-6 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-2 text-blue-900">Import Instructions:</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Download the Excel template for the data type you want to import</li>
            <li>Fill in your data in the template (keep the header row as is)</li>
            <li>Save the file as Excel (.xlsx) format</li>
            <li>Upload and import the file</li>
            <li>Review any errors and fix them in your Excel file</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImportData;
